import styled from 'styled-components';
import { Search } from 'lucide-react';
import { ReactComponent as X12Logo } from '../icons/logo_x12.svg';
import { getStatusColor } from './data';

export const TableContainer = styled.div`
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: 0;
  overflow: hidden;
  margin: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  height: ${({ tableHeight }) => tableHeight ? `${tableHeight}px` : '100%'};
  min-height: 400px;
`;

export const TableWrapper = styled.div`
  flex: 1 1 auto;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const TableScrollContainer = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--border-color) transparent;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--text-muted);
  }
`;

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0rem;
  padding: 0 1rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-navbar);
  flex-wrap: wrap;
  flex-shrink: 0;
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: 2rem;
`;

export const FilterLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const FilterSelect = styled.select`
  background: var(--bg-input);
  border: 1px solid var(--border-input);
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.75rem;
  cursor: pointer;
  min-width: 120px;
  transition: all 0.2s ease;
  appearance: none;
  padding-right: 2.5rem;

  &:focus {
    outline: none;
    border-color: var(--brand-blue);
    box-shadow: 0 0 0 2px rgba(135, 206, 250, 0.2);
  }

  option {
    background: var(--bg-panel);
    color: var(--text-primary);
  }
`;

export const FilterSelectWrapper = styled.div`
  position: relative;
  display: inline-block;

  svg {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    width: 16px;
    height: 16px;
    pointer-events: none;
  }
`;

export const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const DateInput = styled.input`
  background: var(--bg-input);
  border: 1px solid var(--border-input);
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.75rem;
  width: 130px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand-blue);
    box-shadow: 0 0 0 2px rgba(135, 206, 250, 0.2);
  }

  &::-webkit-calendar-picker-indicator {
    filter: ${props => props.theme === 'dark' ? 'invert(0.5)' : 'invert(0.2)'};
    cursor: pointer;
  }

  &::-webkit-datetime-edit {
    color: var(--text-primary);
  }

  &::-webkit-datetime-edit-fields-wrapper {
    color: var(--text-primary);
  }

  &::-webkit-datetime-edit-text {
    color: var(--text-secondary);
  }

  &::-webkit-datetime-edit-month-field,
  &::-webkit-datetime-edit-day-field,
  &::-webkit-datetime-edit-year-field {
    color: var(--text-primary);
  }
`;

export const ClearFiltersButton = styled.button`
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  color: var(--text-secondary);
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    color: var(--text-primary);
    background: var(--bg-hover);
  }
`;

export const ApplyFiltersButton = styled.button`
  background: var(--brand-blue);
  border: 1px solid var(--brand-blue);
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  color: white;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    background: var(--brand-blue);
    border-color: var(--brand-blue);
    color: white;
  }
`;

export const BulkRepriceButton = styled.button`
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: var(--brand-blue);
    background: var(--bg-hover);
    border-color: var(--brand-blue);
    box-shadow: 0 2px 8px rgba(39,145,210,0.08);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.72;
  }
`;

export const BulkRepriceProgress = styled.div`
  position: relative;
  overflow: hidden;
  width: 56px;
  height: 6px;
  border-radius: 999px;
  background: var(--bg-hover);
  border: 1px solid var(--border-color);

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -45%;
    width: 45%;
    border-radius: inherit;
    background: var(--brand-blue);
    animation: bulkRepriceProgress 1s ease-in-out infinite;
  }

  @keyframes bulkRepriceProgress {
    0% {
      left: -45%;
    }
    100% {
      left: 100%;
    }
  }
`;

export const ActiveFiltersCount = styled.span`
  background: var(--brand-blue);
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 0.5rem;
`;

export const TableHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const TableTitle = styled.h3`
  color: var(--text-title);
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

export const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const SearchInput = styled.input`
  background: var(--bg-input);
  border: 1px solid var(--border-input);
  border-radius: 6px;
  padding: 0.5rem 0.75rem 0.5rem 2.5rem;
  color: var(--text-primary);
  font-size: 0.73rem;
  width: 250px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand-blue);
    box-shadow: 0 0 0 2px rgba(135, 206, 250, 0.2);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const SearchIcon = styled(Search)`
  position: absolute;
  left: 0.75rem;
  color: var(--text-muted);
  width: 16px;
  height: 16px;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-panel);
  table-layout: auto;
`;

export const TableHead = styled.thead`
  background: var(--bg-navbar);
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  z-index: 10;

  tr {
    position: sticky;
    top: 0;
    z-index: 10;
  }
`;

export const TableBody = styled.tbody`
  tr:nth-child(even) {
    background: var(--bg-panel);
  }

  tr:nth-child(odd) {
    background: var(--bg-panel-sub);
  }

  tr:hover {
    background: var(--bg-input) !important;
    cursor: pointer;
  }
`;

export const Th = styled.th`
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-title);
  font-size: 0.73rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
  position: relative;
  min-width: ${props => props.minWidth || 'auto'};
  max-width: ${props => props.maxWidth || 'none'};
  width: ${props => props.minWidth || 'auto'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: var(--bg-hover);
  }

  ${props => props.sortable && `
    cursor: pointer;
    &:hover {
      background: var(--bg-hover);
    }
  `}
`;

export const Td = styled.td`
  padding: 0.5rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.73rem;
  border-bottom: 1px solid var(--border-color);
  min-width: ${props => props.minWidth || 'auto'};
  max-width: ${props => props.maxWidth || 'none'};
  width: ${props => props.minWidth || 'auto'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.2s ease;
`;

export const SortIcon = styled.span`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  color: var(--text-muted);
`;

export const FileIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-primary);
  min-width: 0;
`;

export const FileName = styled.span`
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

export const FileType = styled.span`
  color: var(--text-muted);
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
`;

export const UserAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--brand-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.73rem;
  flex-shrink: 0;
`;

export const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

export const UserName = styled.span`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.73rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UserEmail = styled.span`
  color: var(--text-muted);
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DateCell = styled.div`
  color: var(--text-primary);
  font-size: 0.73rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
`;

export const Badge = styled.span`
  padding: 0.22rem 0.44rem;
  border-radius: 5px;
  font-size: 0.64rem;
  font-weight: 600;
  text-transform: capitalize;
  display: inline-block;
  background: ${props => getStatusColor(props.status)};
  color: #333 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  border-top: 1px solid var(--border-color);
  background: var(--bg-panel);
  flex-shrink: 0;
`;

export const PaginationInfo = styled.div`
  color: var(--text-secondary);
  font-size: 0.73rem;
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  background: var(--bg-panel);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.73rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--bg-hover);
    border-color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PageSizeSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  background: var(--bg-panel);
  color: var(--text-primary);
  border-radius: 6px;
  font-size: 0.73rem;
  cursor: pointer;
  appearance: none;
  padding-right: 2.5rem !important;

  &:focus {
    outline: none;
    border-color: var(--brand-blue);
  }

  option {
    background: var(--bg-panel);
    color: var(--text-primary);
  }
`;

export const PageSizeSelectWrapper = styled.div`
  position: relative;
  display: inline-block;

  svg {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    width: 16px;
    height: 16px;
    pointer-events: none;
  }
`;

export const StyledX12Logo = styled(X12Logo)`
  width: 20px;
  height: 20px;
`;
